export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const MODULE_COLORS = {
  Flare: 'bg-orange-100 text-orange-700',
  'Imported Electricity': 'bg-blue-100 text-blue-700',
  'Acid Gas Removal': 'bg-teal-100 text-teal-700',
  'Gas Combustion': 'bg-red-100 text-red-700',
  'Liquid Combustion': 'bg-rose-100 text-rose-700',
  Venting: 'bg-amber-100 text-amber-700',
  'Imported Steam': 'bg-cyan-100 text-cyan-700',
  'Mobile and Transportation': 'bg-green-100 text-green-700',
};

export const moduleBadge = module => MODULE_COLORS[module] ?? 'bg-gray-100 text-gray-600';

// openpyxl (backend) reads .xlsx/.xlsm only; legacy .xls would fail server-side.
export const ACCEPTED_EXTENSIONS = ['.xlsx', '.xlsm'];

export const isAcceptedWorkbook = file => ACCEPTED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));

export const leadingYear = name => name.match(/^\s*(\d{4})/)?.[1] ?? null;

export const byName = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true });

export const formatCo2e = val => {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(2)} Mt`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)} kt`;
  return `${val.toFixed(1)} t`;
};

/**
 * Intensities come through in t CO₂e per unit of flow/production, which puts
 * most real values below 1 and forces scientific notation. Display them in kg.
 */
export const fmtIntensity = v => {
  const kg = v * 1000;
  if (kg >= 10000) return `${(kg / 1000).toFixed(1)}k`;
  if (kg >= 100) return kg.toFixed(0);
  if (kg >= 10) return kg.toFixed(1);
  return kg.toFixed(2);
};

/** Uniform precision across one intensity axis, chosen from its max (in kg). */
/** B.O.P. intensity in t CO₂e per t product; the ratios are small, so they need more decimals. */
export const fmtTPerT = v => {
  const a = Math.abs(v);
  if (a >= 10) return v.toFixed(1);
  if (a >= 1) return v.toFixed(2);
  if (a >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};

export const intensityTickFmt = maxKg => {
  const dp = maxKg >= 100 ? 0 : maxKg >= 10 ? 1 : 2;
  return kg => (kg >= 10000 ? `${(kg / 1000).toFixed(1)}k` : kg.toFixed(dp));
};

export const monthKey = (year, month) => `${year}-${month}`;

/**
 * The backend zero-pads unit_monthly keys ("2025-07") while monthly_data carries
 * numeric year/month. Compare everything through this so the two agree.
 */
export const canonMonthKey = key => {
  const [y, m] = String(key).split('-');
  return monthKey(Number(y), Number(m));
};

/** Sorted list of every month present in the analysis. */
export const buildMonthList = result => {
  const seen = new Map();
  const add = (year, month) => {
    const k = monthKey(year, month);
    if (!seen.has(k)) seen.set(k, { key: k, year, month });
  };
  (result?.hot_spots ?? []).forEach(s => (s.monthly_data ?? []).forEach(d => add(d.year, d.month)));
  (result?.unit_monthly?.months ?? []).forEach(k => {
    const [y, m] = canonMonthKey(k).split('-').map(Number);
    add(y, m);
  });
  return [...seen.values()]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map(m => ({ ...m, label: `${MONTH_NAMES[(m.month - 1) % 12]} ${m.year}` }));
};

export const stdDev = vals => {
  if (vals.length < 2) return 0;
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  return Math.sqrt(vals.reduce((a, v) => a + (v - mean) ** 2, 0) / (vals.length - 1));
};

/**
 * Re-derive the analysis restricted to `allowedKeys` months.
 * Returns `result` untouched when the full range is selected.
 */
export const sliceResultByMonths = (result, allowedKeys) => {
  if (!allowedKeys) return result;
  const keep = new Set(allowedKeys);
  const inRange = d => keep.has(monthKey(d.year, d.month));

  const hot_spots = (result.hot_spots ?? [])
    .map(s => {
      const monthly = (s.monthly_data ?? []).filter(inRange);
      const total = monthly.reduce((a, d) => a + d.co2e, 0);
      return {
        ...s,
        monthly_data: monthly,
        total_co2e: total,
        avg_monthly_co2e: monthly.length ? total / monthly.length : 0,
      };
    })
    .filter(s => s.monthly_data.length > 0);

  // unit_monthly and module_monthly share a shape, so slice them the same way
  const sliceMonthly = um => {
    if (!um?.months?.length) return um;
    const idxs = um.months.map((k, i) => (keep.has(canonMonthKey(k)) ? i : -1)).filter(i => i >= 0);
    return {
      ...um,
      months: idxs.map(i => um.months[i]),
      units: (um.units ?? []).map(u => {
        const values = idxs.map(i => u.values?.[i] ?? 0);
        return {
          ...u,
          values,
          production: idxs.map(i => u.production?.[i] ?? 0),
          total: values.reduce((a, b) => a + b, 0),
        };
      }),
    };
  };
  const unit_monthly = sliceMonthly(result.unit_monthly);
  const module_monthly = sliceMonthly(result.module_monthly);

  const usi = result.unit_source_intensity;
  const unit_source_intensity = usi?.months?.length
    ? (() => {
        const idxs = usi.months.map((k, i) => (keep.has(canonMonthKey(k)) ? i : -1)).filter(i => i >= 0);
        return {
          months: idxs.map(i => usi.months[i]),
          sources: usi.sources.map(src => ({
            ...src,
            intensity_kg_per_t: idxs.map(i => src.intensity_kg_per_t?.[i] ?? null),
          })),
        };
      })()
    : usi;

  const pb = result.production_breakdown;
  const production_breakdown = pb?.months?.length
    ? (() => {
        const idxs = pb.months.map((k, i) => (keep.has(canonMonthKey(k)) ? i : -1)).filter(i => i >= 0);
        const pick = vals => idxs.map(i => vals?.[i] ?? 0);
        return {
          ...pb,
          months: idxs.map(i => pb.months[i]),
          units: pb.units.map(u => ({
            ...u,
            total: pick(u.total),
            components: Object.fromEntries(Object.entries(u.components).map(([c, vals]) => [c, pick(vals)])),
          })),
        };
      })()
    : pb;

  // hot_spots is only the top 25, so totals come from the all-source series
  const mt = result.monthly_totals;
  const mtIdxs = (mt?.months ?? []).map((k, i) => (keep.has(canonMonthKey(k)) ? i : -1)).filter(i => i >= 0);
  const sumInRange = vals => mtIdxs.reduce((a, i) => a + (vals?.[i] ?? 0), 0);
  const topInRange = series =>
    Object.entries(series ?? {})
      .map(([name, vals]) => ({ name, total_co2e: sumInRange(vals) }))
      .filter(x => x.total_co2e > 0)
      .sort((a, b) => b.total_co2e - a.total_co2e)
      .slice(0, 6);

  const levelized_sources = (result.levelized_sources ?? [])
    .map(s => {
      const rows = (s.monthly_levelized ?? []).filter(inRange);
      const iv = rows.map(r => r.intensity).filter(v => v != null);
      const mean = iv.length ? iv.reduce((a, b) => a + b, 0) / iv.length : 0;
      const sd = stdDev(iv);
      return {
        ...s,
        monthly_levelized: rows,
        variability: sd,
        cv: mean > 0 ? sd / mean : 0,
        anomaly_count: rows.filter(r => r.is_anomaly).length,
      };
    })
    .filter(s => s.monthly_levelized.length > 0);

  return {
    ...result,
    hot_spots,
    unit_monthly,
    module_monthly,
    unit_source_intensity,
    production_breakdown,
    levelized_sources,
    total_co2e: sumInRange(mt?.facility),
    top_modules: topInRange(mt?.by_module),
    top_locations: topInRange(mt?.by_location),
  };
};

/** CSV of every source in the file — the basis for mapping equipment to pathway nodes. */
export const downloadSourceInventory = result => {
  const rows = result?.source_inventory ?? [];
  if (!rows.length) return;
  const cols = ['module', 'source_name', 'unit', 'location', 'sub_location', 'scope', 'total_co2e'];
  const esc = v => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [
    cols.join(','),
    ...rows.map(r => cols.map(c => esc(c === 'total_co2e' ? Number(r[c] ?? 0).toFixed(2) : r[c])).join(',')),
  ].join('\n');

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'gcon-equipment-inventory.csv';
  a.click();
  URL.revokeObjectURL(url);
};

export const UNIT_COLOR_MAP = {
  'GPP 2': '#00A99D',
  'GPP 3': '#2D7CFF',
  'GPP 4': '#FF8F12',
  COGEN: '#A694FF',
  KCS: '#02AD74',
  'DPCU 2': '#f43f5e',
};

export const UNIT_COLOR_FALLBACK = ['#f59e0b', '#ec4899', '#6366f1', '#64748b'];

export const unitColor = name => UNIT_COLOR_MAP[name] ?? UNIT_COLOR_FALLBACK[0];

/** Units ineligible for the cross-unit "best performer" benchmark. */
export const normUnit = s => (s ?? '').replace(/[^a-z0-9]/gi, '').toUpperCase();

export const BOP_BENCHMARK_EXCLUDED = new Set(['DPCU2']);

export const fmtMonthKey = key => {
  const [y, m] = key.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} '${y.slice(2)}`;
};

/**
 * Split a unit's monthly intensities into quartiles (Q1 = lowest intensity =
 * best) and benchmark actual performance against the Q1 average.
 */
export const buildBopRow = (label, obs) => {
  const sorted = [...obs].sort((a, b) => a.intensity - b.intensity);
  const n = sorted.length;
  const quartiles = [[], [], [], []];
  sorted.forEach((o, i) => quartiles[Math.min(3, Math.floor((i * 4) / n))].push(o));

  const mean = arr => arr.reduce((s, o) => s + o.intensity, 0) / arr.length;
  const qAvgs = quartiles.map(q => (q.length ? mean(q) : null));

  const totalCo2e = obs.reduce((s, o) => s + o.co2e, 0);
  const totalProd = obs.reduce((s, o) => s + o.production, 0);
  const actual = totalProd > 0 ? totalCo2e / totalProd : 0;
  const best = qAvgs[0];
  const bopCo2e = best * totalProd;
  const savings = Math.max(totalCo2e - bopCo2e, 0);

  return {
    label,
    obs,
    quartiles,
    qAvgs,
    totalCo2e,
    totalProd,
    actual,
    best,
    worst: qAvgs[3],
    bopCo2e,
    savings,
    gapPct: totalCo2e > 0 ? (savings / totalCo2e) * 100 : 0,
    spreadPct: best > 0 && qAvgs[3] != null ? ((qAvgs[3] - best) / best) * 100 : 0,
    bestMonths: quartiles[0].map(o => o.key),
  };
};

export const buildBopData = data => {
  const { months = [], units = [], production_basis: basis } = data ?? {};
  // Categories all carry the same plant-wide production, so the combined row
  // must take that denominator once rather than summing it per group.
  const sharedDenominator = basis === 'plant-wide';

  const obsFor = u =>
    months
      .map((key, i) => ({
        key,
        co2e: u.values?.[i] ?? 0,
        production: u.production?.[i] ?? 0,
      }))
      .filter(o => o.production > 0 && o.co2e > 0)
      .map(o => ({ ...o, intensity: o.co2e / o.production }));

  const rows = units
    .map(u => ({ u, obs: obsFor(u) }))
    .filter(({ obs }) => obs.length >= 4)
    .map(({ u, obs }) => buildBopRow(u.location, obs))
    .sort((a, b) => b.savings - a.savings);

  // Plant-wide: total emissions / total production for each month across groups
  const plantObs = months
    .map((key, i) => ({
      key,
      co2e: units.reduce((s, u) => s + (u.values?.[i] ?? 0), 0),
      production: sharedDenominator
        ? (units[0]?.production?.[i] ?? 0)
        : units.reduce((s, u) => s + (u.production?.[i] ?? 0), 0),
    }))
    .filter(o => o.production > 0 && o.co2e > 0)
    .map(o => ({ ...o, intensity: o.co2e / o.production }));

  const plant = plantObs.length >= 4 ? buildBopRow('All units combined', plantObs) : null;

  return { rows, plant };
};

/**
 * What the model gets: the filtered analysis, exactly which months it covers,
 * and the B.O.P. benchmark — which is derived in the UI, so without this the
 * model would be blind to the numbers the user is looking at.
 */
export const buildAiContext = (view, selection, targets = {}) => {
  if (!view) return view;
  const { rows, plant } = buildBopData(view.unit_monthly);
  const byCategory = buildBopData(view.module_monthly);
  const tPerT = v => (v == null ? null : +v.toFixed(6));
  const targetYear = targets.year ?? 2030;
  const withTarget = (summary, actual, target) =>
    target == null
      ? summary
      : {
          ...summary,
          target_year: targetYear,
          target_intensity_t_per_t: tPerT(target),
          above_target_pct: target > 0 ? +(((actual - target) / target) * 100).toFixed(1) : null,
        };
  const summarize = r => ({
    unit: r.label,
    months_analysed: r.obs.length,
    quartile_avg_intensity_t_per_t: r.qAvgs.map(tPerT),
    best_quartile_intensity_t_per_t: tPerT(r.best),
    worst_quartile_intensity_t_per_t: tPerT(r.worst),
    actual_intensity_t_per_t: tPerT(r.actual),
    gap_to_best_pct: +r.gapPct.toFixed(1),
    spread_q1_to_q4_pct: +r.spreadPct.toFixed(1),
    savings_t: +r.savings.toFixed(1),
    total_co2e_t: +r.totalCo2e.toFixed(1),
    total_production: +r.totalProd.toFixed(1),
    best_months: r.bestMonths,
  });

  return {
    ...view,
    selection,
    bop: {
      basis: "Q1 is the lowest-intensity quartile of that unit's own months — performance it has already demonstrated.",
      plant: plant ? withTarget(summarize(plant), plant.actual, targets.plant) : null,
      units: rows.map(summarize),
      // Same benchmark grouped by emissions category. Categories span units, so
      // their intensity denominator is plant-wide production, not a unit's.
      by_category: byCategory.rows.map(r => withTarget(summarize(r), r.actual, targets.by_category?.[r.label])),
      category_plant: byCategory.plant
        ? withTarget(summarize(byCategory.plant), byCategory.plant.actual, targets.plant)
        : null,
    },
  };
};

/** Key under which insights for a month selection are saved on the session. */
export const insightsKey = selection => (selection?.months_included ?? []).join(',');

const monthLabel = (year, month) => `${MONTH_NAMES[(month - 1) % 12]} ${year}`;

/**
 * Starter questions for an empty chat. Three are fixed; the last points at the
 * top-ranked source that has a statistical-outlier month, when there is one.
 */
export const starterQuestions = result => {
  const questions = [
    'Which unit has the largest B.O.P. opportunity, and which months set its best quartile?',
    "Which sources' intensity got worse over the period, and by how much?",
    "Rank the flares by emissions per tonne of their unit's production.",
  ];
  const outlierMonth = new Map(
    (result?.levelized_sources ?? []).map(s => [s.source_id, s.monthly_levelized?.find(r => r.is_anomaly)]),
  );
  const spot = (result?.hot_spots ?? []).find(s => outlierMonth.get(s.source_id));
  if (spot) {
    const row = outlierMonth.get(spot.source_id);
    questions.push(`Why does ${spot.source_name} stand out in ${monthLabel(row.year, row.month)}?`);
  } else {
    questions.push('Which months were statistical outliers, and for which sources?');
  }
  return questions;
};

/** Stable colour per emissions category, reusing the module badge palette. */
export const MODULE_DOT_COLORS = {
  Flare: '#FF8F12',
  'Imported Electricity': '#2D7CFF',
  'Acid Gas Removal': '#00A99D',
  'Gas Combustion': '#DC2626',
  'Liquid Combustion': '#f43f5e',
  Venting: '#FBBF24',
  'Imported Steam': '#06b6d4',
  'Mobile and Transportation': '#02AD74',
};

export const MODULE_DOT_FALLBACK = ['#A694FF', '#64748b', '#ec4899', '#6366f1'];

export const moduleColor = name =>
  MODULE_DOT_COLORS[name] ??
  MODULE_DOT_FALLBACK[
    Math.abs([...(name ?? '')].reduce((h, c) => h + c.charCodeAt(0), 0)) % MODULE_DOT_FALLBACK.length
  ];

const transcriptTime = iso => (iso ? new Date(iso).toLocaleString() : '');

/** Plain-text transcript of a session's saved AI chat. */
export const buildChatTranscript = (sessionName, messages) => {
  const lines = [`GCON analysis chat — ${sessionName}`, `Exported ${new Date().toLocaleString()}`, ''];
  messages.forEach(m => {
    const who = m.role === 'user' ? 'You' : 'Sesame Analyst';
    lines.push(`${who} (${transcriptTime(m.created_at)}):`, m.content.trim(), '');
  });
  return lines.join('\n');
};

export const downloadChatTranscript = (sessionName, messages) => {
  const slug = sessionName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'gcon';
  const url = URL.createObjectURL(
    new Blob([buildChatTranscript(sessionName, messages)], { type: 'text/plain;charset=utf-8;' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug}-chat.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

export const EXAMPLE_YEAR = 2024;

/**
 * Group the chosen files by the year their names start with and say what's
 * wrong, mirroring the server's pairing rules:
 * - every Input needs the Output for its year;
 * - a year may have only an Output — its production then comes from the
 *   Output's Intensity sheet and it has no flow levelization (a notice, not an
 *   error);
 * - with more than one file, Input names must start with their year. A lone
 *   pair whose names carry no year is exempt (its year comes from the Output).
 */
export const planGconUpload = (inputFiles, outputFiles) => {
  const years = new Map();
  const row = year => {
    if (!years.has(year)) years.set(year, { year, input: [], output: [] });
    return years.get(year);
  };
  inputFiles.forEach(f => row(leadingYear(f.name)).input.push(f));
  outputFiles.forEach(f => row(leadingYear(f.name)).output.push(f));
  const rows = [...years.values()].sort((a, b) => (a.year ?? '9999').localeCompare(b.year ?? '9999'));

  const singlePair = inputFiles.length === 1 && outputFiles.length === 1;
  const unnamedInputs = inputFiles.filter(f => !leadingYear(f.name));
  const unnamedOutputs = outputFiles.filter(f => !leadingYear(f.name));
  // A lone unnamed Input/Output may still cover the year named on the other side
  const loneUnnamedInput = singlePair && unnamedInputs.length === 1;
  const loneUnnamedOutput = singlePair && unnamedOutputs.length === 1;

  let problem = null;
  if (outputFiles.length) {
    const duplicate = rows.find(r => r.year && r.input.length > 1);
    const noOutput = rows.find(r => r.year && r.input.length && !r.output.length);
    if (!singlePair && unnamedInputs.length) {
      problem = `Can't tell which year "${unnamedInputs[0].name}" is for. Rename it to start with its year, e.g. "${EXAMPLE_YEAR} Gcon Input.xlsx".`;
    } else if (duplicate) {
      problem = `There are two Input files for ${duplicate.year}. Remove one — each year needs at most one Input.`;
    } else if (noOutput && !loneUnnamedOutput && !(unnamedOutputs.length && !singlePair)) {
      problem = `${noOutput.year} has an Input file but no Output file. Add the ${noOutput.year} Gcon Output.`;
    }
  }

  const outputOnlyYears = loneUnnamedInput
    ? []
    : rows.filter(r => r.year && r.output.length && !r.input.length).map(r => r.year);
  return { rows, singlePair, problem, outputOnlyYears };
};
